import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../App";
import { Button } from "../ui/button";
import {
  LayoutDashboard,
  FileText,
  MessageSquare,
  LogOut,
  Settings,
  Calendar,
  Briefcase,
  ChevronDown,
  Building2,
  TrendingUp,
  PanelLeftClose,
  PanelLeft,
  Search,
  Menu,
  X,
} from "lucide-react";

const ClientLayout = ({ children }) => {
  const { user, logout, API, token } = useAuth();
  const location = useLocation();
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const checkCompanyAdmin = async () => {
      try {
        const response = await fetch(`${API}/companies/my-company`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setIsCompanyAdmin(data.is_company_admin === true);
        }
      } catch (error) {
        console.error("Request failed:", error);
      }
    };
    if (token) checkCompanyAdmin();
  }, [API, token]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navSections = () => {
    const sections = [
      {
        items: [
          { name: "Dashboard", path: "/portal", icon: LayoutDashboard },
        ],
      },
      {
        label: "Work",
        items: [
          { name: "Projects", path: "/portal/my-projects", icon: Briefcase },
          { name: "Updates", path: "/portal/updates", icon: TrendingUp },
        ],
      },
      {
        label: "Billing",
        items: [
          { name: "Invoices", path: "/portal/invoices", icon: FileText },
        ],
      },
      {
        label: "Support",
        items: [
          { name: "Chat", path: "/portal/chat", icon: MessageSquare },
          { name: "Book Appointment", path: "/portal/booking", icon: Calendar },
        ],
      },
    ];

    if (isCompanyAdmin) {
      sections[1].items.push({ name: "Company", path: "/portal/company", icon: Building2 });
    }

    sections.push({
      items: [
        { name: "Settings", path: "/portal/settings", icon: Settings },
      ],
    });

    return sections;
  };

  useEffect(() => {
    const sections = navSections();
    const expanded = {};
    sections.forEach((section) => {
      if (section.label) {
        const hasActive = section.items.some(
          (item) =>
            location.pathname === item.path ||
            (item.path !== "/portal" && location.pathname.startsWith(item.path))
        );
        if (hasActive) expanded[section.label] = true;
      }
    });
    setExpandedSections((prev) => ({ ...prev, ...expanded }));
  }, [location.pathname, isCompanyAdmin]);

  const toggleSection = (label) => {
    setExpandedSections((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isActive = (path) =>
    location.pathname === path ||
    (path !== "/portal" && location.pathname.startsWith(path));

  const sections = navSections();

  const filteredSections = searchQuery
    ? sections
        .map((s) => ({
          ...s,
          items: s.items.filter((i) =>
            i.name.toLowerCase().includes(searchQuery.toLowerCase())
          ),
        }))
        .filter((s) => s.items.length > 0)
    : sections;

  // Shared sidebar content
  const SidebarContent = ({ isMobile }) => (
    <>
      {(!collapsed || isMobile) && (
        <div className="px-3 pt-3 pb-1">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search menu..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg text-rnb-navy placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-rnb-blue/30 focus:border-rnb-blue/30"
              data-testid="sidebar-search"
            />
          </div>
        </div>
      )}

      <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto">
        {filteredSections.map((section, si) => (
          <div key={section.label || `section-${si}`}>
            {section.label && (!collapsed || isMobile) && (
              <button
                className="w-full flex items-center justify-between px-3 py-2 mt-3 first:mt-0 text-[11px] font-semibold text-gray-400 uppercase tracking-wider hover:text-rnb-blue transition-colors"
                onClick={() => toggleSection(section.label)}
                data-testid={`nav-section-${section.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {section.label}
                <ChevronDown
                  className={`w-3 h-3 transition-transform duration-200 ${
                    expandedSections[section.label] === false ? "-rotate-90" : ""
                  }`}
                />
              </button>
            )}

            {section.label && collapsed && !isMobile && (
              <div className="mx-3 my-2 border-t border-gray-100" />
            )}

            <div
              className={`space-y-0.5 ${
                section.label && (!collapsed || isMobile) && expandedSections[section.label] === false
                  ? "hidden"
                  : ""
              }`}
            >
              {section.items.map((item) => {
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2.5 rounded-lg transition-all duration-150 group relative ${
                      collapsed && !isMobile ? "justify-center px-0 py-2.5 mx-1" : "px-3 py-2"
                    } ${
                      active
                        ? "bg-gradient-to-r from-rnb-navy to-rnb-blue text-white shadow-sm"
                        : "text-gray-600 hover:bg-rnb-light hover:text-rnb-navy"
                    }`}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                    title={collapsed && !isMobile ? item.name : undefined}
                  >
                    <item.icon
                      className={`shrink-0 ${collapsed && !isMobile ? "w-[18px] h-[18px]" : "w-4 h-4"} ${
                        active ? "text-white" : "text-gray-400 group-hover:text-rnb-blue"
                      }`}
                    />
                    {(!collapsed || isMobile) && (
                      <span className="text-[13px] font-medium truncate">{item.name}</span>
                    )}
                    {collapsed && !isMobile && (
                      <div className="absolute left-full ml-2 px-2 py-1 bg-rnb-navy text-white text-xs rounded-md whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50 shadow-lg">
                        {item.name}
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
    </>
  );

  return (
    <div className="min-h-screen bg-[#f0f4f8]">
      {/* Top Bar */}
      <header className="bg-rnb-navy sticky top-0 z-50 shadow-md">
        <div className="flex items-center justify-between h-14 px-4 lg:px-6">
          <div className="flex items-center gap-3">
            {/* Desktop sidebar toggle */}
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="hidden lg:block p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="sidebar-toggle"
            >
              {collapsed ? <PanelLeft className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
            </button>
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Link to="/" className="flex items-center gap-2.5" data-testid="portal-logo">
              <div className="w-8 h-8 bg-white/15 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/20">
                <span className="text-white font-bold text-sm">R</span>
              </div>
              <span className="font-heading font-bold text-white text-sm hidden sm:block tracking-wide">
                Client Portal
              </span>
            </Link>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2.5 pl-3 ml-1 border-l border-white/20">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rnb-teal to-rnb-blue flex items-center justify-center text-white text-xs font-bold ring-2 ring-white/20">
                {user?.name?.charAt(0) || "U"}
              </div>
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-white leading-none">{user?.name || "User"}</p>
                <span className="text-[11px] text-white/50">Client</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="rounded-lg w-9 h-9 p-0 text-white/50 hover:text-white hover:bg-white/10"
                data-testid="portal-logout"
              >
                <LogOut className="h-[18px] w-[18px]" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex relative">
        {/* Mobile overlay */}
        {mobileOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-40 lg:hidden"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-overlay"
          />
        )}

        {/* Mobile sidebar drawer */}
        <aside
          className={`fixed top-14 left-0 bottom-0 w-64 bg-white border-r border-gray-200/60 z-50 transform transition-transform duration-300 ease-out lg:hidden flex flex-col shadow-xl ${
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          }`}
          data-testid="mobile-sidebar"
        >
          <SidebarContent isMobile={true} />
        </aside>

        {/* Desktop sidebar */}
        <aside
          className={`${
            collapsed ? "w-[68px]" : "w-60"
          } bg-white border-r border-gray-200/60 min-h-[calc(100vh-56px)] sticky top-14 transition-all duration-300 hidden lg:flex flex-col shadow-sm`}
          data-testid="portal-sidebar"
        >
          <SidebarContent isMobile={false} />
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ClientLayout;
